import { CommonActions } from '@react-navigation/native';
import { checkPushNotificationsEnabled } from 'utils/packages/pushNotifications';
import checkFeatureFlag from 'utils/featureFlags';
import { isConnectionBroken, isConnectionExpiring, } from 'features/settings/logins/utils';
import { isEligibleForOnboardingSetup, isOnboardingReportEnabled, isOnboardingSetupEnabled, } from 'features/onboarding-report/cohort';
import { isEligibleForMiniValueProof } from 'features/onboarding-insights/cohort';
import { isReady } from 'features/onboarding-insights/selectors';
const defaultParams = { back: false };
export const getBankConnectionsRouteForUser = ({ user, connectingSecond, connectAllBanks, }) => {
    if (user.guessedHomeCountry === 'GB') {
        return {
            name: 'ChooseBank',
            params: {
                ...defaultParams,
                personal: true,
                pushRoute: connectingSecond,
                connectAllBanks,
            },
        };
    }
    return {
        name: 'USConsent',
        params: { back: false },
    };
};
const getBankConnectionsRoute = ({ user, connections, connectingConnection, signInNavigation, featureFlags, }) => {
    if (!connectingConnection && !connections.length) {
        const removeScreensV2 = checkFeatureFlag(featureFlags, 'ab_remove_screens_v2');
        if (user.guessedHomeCountry === 'GB' &&
            !checkFeatureFlag(featureFlags, 'ab_remove_screens') &&
            !removeScreensV2) {
            if (checkFeatureFlag(featureFlags, 'ab_choose_bank_intro_animated')) {
                return {
                    params: undefined,
                    name: 'ChooseBankIntroAnimated',
                };
            }
            return {
                params: undefined,
                name: 'ChooseBankIntro',
            };
        }
        if (user.guessedHomeCountry === 'GB' && removeScreensV2) {
            return {
                params: undefined,
                name: 'ConsentModal',
            };
        }
        return getBankConnectionsRouteForUser({ user });
    }
    if (!connectingConnection && !signInNavigation.refreshConnectionViewed) {
        if (connections.some(user.guessedHomeCountry === 'GB'
            ? isConnectionExpiring
            : isConnectionBroken)) {
            return {
                params: undefined,
                name: 'RefreshConnectionScreen',
            };
        }
    }
    if (!signInNavigation.connectingSecondLogin &&
        !signInNavigation.secondLoginSkipped &&
        (connections.length === 1 || connectingConnection)) {
        if (!signInNavigation.didSetHasSecondLogin &&
            !signInNavigation.isSigningIn) {
            return {
                params: undefined,
                name: 'AskAnotherBankAccount',
            };
        }
        return {
            params: undefined,
            name: 'LinkAnotherLogin',
        };
    }
    return null;
};
const getPushNotificationsRoute = async (signInNavigation) => {
    if (!signInNavigation.pushNotificationsSkipped) {
        const pushNotificationsEnabled = await checkPushNotificationsEnabled();
        if (!pushNotificationsEnabled) {
            return {
                params: undefined,
                name: 'PushNotifications',
            };
        }
    }
    return null;
};
const getFirstPremiumScreenRoute = ({ user, signInNavigation, isSecond, featureFlags, shouldShowStripe, hasEmployerBenefit, }) => {
    if (hasEmployerBenefit)
        return null;
    if (user.emmaProStatus) {
        if (!user.emmaProStatus.active) {
            if (!signInNavigation.premiumScreenSkipped) {
                if (user.emmaProStatus.referrerName &&
                    user.emmaProStatus.eligibleFreeTrialDays &&
                    user.emmaProStatus.eligibleFreeTrialDays > 7) {
                    return {
                        name: 'OnboardingPremiumReferralScreen',
                        params: undefined,
                    };
                }
                if (checkFeatureFlag(featureFlags, 'step_by_step_paywall_f2') &&
                    user.guessedHomeCountry === 'GB' &&
                    !signInNavigation.didSeeMyFreeOffer) {
                    return {
                        name: 'OnboardingMyFreeOfferScreen',
                        params: undefined,
                    };
                }
                if (checkFeatureFlag(featureFlags, 'step_by_step_paywall_f2') &&
                    user.guessedHomeCountry === 'GB' &&
                    !signInNavigation.didSeeMyFreeOfferDiscount) {
                    return {
                        name: 'OnboardingMyFreeOfferDiscountScreen',
                        params: undefined,
                    };
                }
                return {
                    name: 'OnboardingHowFreeTrialWorksScreen',
                    params: {},
                };
            }
            const offerFlagPayload = featureFlags.ab_discount_after_paywall?.extra;
            if (shouldShowStripe &&
                !user.emmaProStatus.usedFreeTrial &&
                user.emmaProStatus.eligibleFreeTrialDays &&
                !signInNavigation.premiumScreenWelcomeGiftSkipped &&
                checkFeatureFlag(featureFlags, 'ab_discount_after_paywall') &&
                offerFlagPayload?.[user.guessedHomeCountry]) {
                const { currency, ...stripeOffer } = offerFlagPayload[user.guessedHomeCountry];
                return {
                    name: 'OnboardingPremiumWelcomeGiftScreen',
                    params: { currency, stripeOffer },
                };
            }
            if (isSecond &&
                !signInNavigation.premiumScreenSkippedSecond &&
                !signInNavigation.isSigningIn) {
                return {
                    params: {},
                    name: 'SecondOnboardingHowFreeTrialWorksScreen',
                };
            }
        }
        else if (!signInNavigation.viewedPremiumWelcomeScreen &&
            signInNavigation.hasUpgradedToPremium) {
            return {
                params: { tier: user.emmaProStatus.tier },
                name: 'OnboardingPremiumSubscriptionWelcomeScreen',
            };
        }
    }
    return null;
};
export const getSetupRoute = ({ user, featureFlags, signInNavigation, }) => {
    if (!isOnboardingSetupEnabled(featureFlags) ||
        !isEligibleForOnboardingSetup({ user, signInNavigation })) {
        return null;
    }
    if (!signInNavigation.didSetupLinkAccounts) {
        return {
            name: 'SetupAllYourMoneyScreen',
            params: undefined,
        };
    }
    if (!signInNavigation.didSetupBudget) {
        return {
            name: 'SetupBudgetScreen',
            params: undefined,
        };
    }
    // The `ab_onboarding_report` variant drops the subscriptions step
    if (!isOnboardingReportEnabled(featureFlags) &&
        !signInNavigation.didSetupSubscriptions) {
        return {
            name: 'SetupSubscriptionsScreen',
            params: undefined,
        };
    }
    return null;
};
export const initialRouteName = 'OnBoardingScreen';
// flow can be seen here https://www.figma.com/file/axHgXIQEADNneEgpzPdx1G/Untitled?node-id=0%3A1
export async function getNextRoute({ user, userAddresses, userAdditionalInfo, connections, connectingConnection = false, isAppStart = false, storePasscode, featureFlags, signInNavigation, resetPasscodeNextStep, hasCurrentRentReporting, spaces, shouldShowStripe, hasEmployerBenefit, spendingInsights, }) {
    if (isAppStart) {
        if (user.pinLastChangedAt === null ||
            resetPasscodeNextStep === 'set_new_pin') {
            return {
                params: defaultParams,
                name: 'SetupPasscode',
            };
        }
        if (!user.pinLastChangedAt) {
            return {
                params: undefined,
                name: initialRouteName,
            };
        }
    }
    if (!isAppStart &&
        (!user.pinLastChangedAt || resetPasscodeNextStep === 'set_new_pin')) {
        return {
            name: 'SetupPasscode',
            params: defaultParams,
        };
    }
    if (!user.firstName || !user.lastName) {
        return {
            params: undefined,
            name: 'Name',
        };
    }
    if (!user.dateOfBirth) {
        return {
            params: {},
            name: 'DateOfBirth',
        };
    }
    // If the user previously authenticated with apple/google we may have their email already
    if (!user.email) {
        return {
            params: undefined,
            name: 'Email',
        };
    }
    if (checkFeatureFlag(featureFlags, 'onboarding_address') &&
        user.guessedHomeCountry === 'GB' &&
        !userAddresses?.length) {
        return {
            params: {
                skipGetAddress: isAppStart,
            },
            name: 'AddressPostCodeScreen',
        };
    }
    if (checkFeatureFlag(featureFlags, 'onboarding_kyc_v2') &&
        user.guessedHomeCountry === 'GB') {
        if (!userAdditionalInfo?.maritalStatus &&
            !signInNavigation.didPostMaritalStatus) {
            return {
                params: undefined,
                name: 'MaritalStatusScreen',
            };
        }
        if ((userAdditionalInfo?.dependantNumber === undefined ||
            userAdditionalInfo?.dependantNumber === null) &&
            !signInNavigation.didPostDependantNumber) {
            return {
                params: undefined,
                name: 'DependentsScreen',
            };
        }
        if (!userAdditionalInfo?.employmentStatus &&
            !signInNavigation.didPostEmploymentStatus) {
            return {
                params: undefined,
                name: 'EmploymentStatusScreen',
            };
        }
        if ((userAdditionalInfo?.grossAnnualSalary === undefined ||
            userAdditionalInfo?.grossAnnualSalary === null) &&
            !signInNavigation.didPostGrossAnnualSalary) {
            return {
                params: undefined,
                name: 'AnnualGrossIncomeScreen',
            };
        }
    }
    const hasNotConnectedBank = !connectingConnection && !connections.length;
    const showCustomiseEmmaExperienceQuestions = (featureFlags.onboarding_customise_emma_experience_questions?.value ===
        'yes' ||
        featureFlags.onboarding_customise_emma_experience_questions?.value ===
            'v2' ||
        featureFlags.onboarding_customise_emma_experience_questions?.value ===
            'v3') &&
        user.guessedHomeCountry === 'GB' &&
        !signInNavigation.isSigningIn;
    const isCustomiseEmmaExperienceV3 = featureFlags.onboarding_customise_emma_experience_questions?.value === 'v3';
    // the stuffs below will have been filled after the user has connected bank
    if (hasNotConnectedBank) {
        const isOnboardingProfilingQuestionsEnabled = featureFlags.onboarding_emma_profiling_questions?.value === 'yes';
        if (!showCustomiseEmmaExperienceQuestions) {
            if (!isOnboardingProfilingQuestionsEnabled &&
                userAdditionalInfo?.isRenting == null &&
                !hasCurrentRentReporting) {
                return {
                    params: undefined,
                    name: 'RentQuestionScreen',
                };
            }
            if (isOnboardingProfilingQuestionsEnabled) {
                if (!signInNavigation.profilingQuestions?.didComplete) {
                    return {
                        params: undefined,
                        name: 'ProfilingQuestions',
                    };
                }
                if (!signInNavigation.didSetAttribution) {
                    return {
                        params: undefined,
                        name: 'Attribution',
                    };
                }
            }
            else if (!userAdditionalInfo?.financialGoals?.length) {
                return {
                    params: undefined,
                    name: 'SetGoal',
                };
            }
        }
        else if (!signInNavigation.didSetAttribution) {
            return {
                params: undefined,
                name: 'Attribution',
            };
        }
    }
    const pushNotificationsRoute = await getPushNotificationsRoute(signInNavigation);
    if (pushNotificationsRoute) {
        return pushNotificationsRoute;
    }
    if (showCustomiseEmmaExperienceQuestions) {
        if (!signInNavigation.customizeEmmaExperience?.didSeeIntro) {
            return {
                params: undefined,
                name: 'CustomiseExperienceIntro',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.financialFeeling) {
            return {
                params: undefined,
                name: 'FinancialFeelingScreen',
            };
        }
        if (isCustomiseEmmaExperienceV3 &&
            (signInNavigation.customizeEmmaExperience?.financialFeeling ===
                'clueless' ||
                signInNavigation.customizeEmmaExperience?.financialFeeling ===
                    'could_do_better') &&
            !signInNavigation.customizeEmmaExperience?.resonanceType &&
            !signInNavigation.customizeEmmaExperience
                ?.didSeeFinancialFeelingPositiveReinforcementScreen) {
            return {
                params: undefined,
                name: 'FinancialFeelingPositiveReinforcementScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.resonanceType) {
            return {
                params: undefined,
                name: 'ResonanceSelectionScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.moneyManagementStyle) {
            return {
                params: undefined,
                name: 'MoneyManagementStyleScreen',
            };
        }
        if (isCustomiseEmmaExperienceV3 &&
            signInNavigation.customizeEmmaExperience?.moneyManagementStyle !==
                'skip' &&
            !signInNavigation.customizeEmmaExperience?.paydayConcern &&
            !signInNavigation.customizeEmmaExperience
                ?.didSeeMoneyManagementStylePositiveReinforcementScreen) {
            return {
                params: undefined,
                name: 'MoneyManagementStylePositiveReinforcementScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.paydayConcern) {
            return {
                params: undefined,
                name: 'PaydayConcernScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.spendingTrackingType) {
            return {
                params: undefined,
                name: 'SpendingTrackingScreen',
            };
        }
        if (typeof signInNavigation.customizeEmmaExperience?.sharesExpense !==
            'boolean' &&
            signInNavigation.customizeEmmaExperience?.sharesExpense !== 'skip') {
            return {
                params: undefined,
                name: 'ExpenseSharingScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.numberOfFinancialAccounts) {
            return {
                params: undefined,
                name: 'FinancialAccountsScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.savingFrequency) {
            return {
                params: undefined,
                name: 'SavingFrequencyScreen',
            };
        }
        if (isCustomiseEmmaExperienceV3 &&
            signInNavigation.customizeEmmaExperience?.savingFrequency !== 'skip' &&
            !signInNavigation.customizeEmmaExperience?.useOfEmma &&
            !signInNavigation.customizeEmmaExperience
                ?.didSeeSaveMoneyPositiveReinforcementScreen) {
            return {
                params: undefined,
                name: 'SaveMoneyPositiveReinforcementScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.useOfEmma) {
            return {
                params: undefined,
                name: 'EmmaSavingHelpScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.investmentStatus) {
            return {
                params: undefined,
                name: 'InvestmentStatusScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.rentOrOwn) {
            return {
                params: undefined,
                name: 'RentOrOwnScreen',
            };
        }
        if (signInNavigation.customizeEmmaExperience?.rentOrOwn === 'own' &&
            typeof signInNavigation.customizeEmmaExperience?.hasMortgage !==
                'boolean' &&
            signInNavigation.customizeEmmaExperience?.hasMortgage !== 'skip') {
            return {
                params: undefined,
                name: 'MortgageInfoScreen',
            };
        }
        if (signInNavigation.customizeEmmaExperience?.rentOrOwn === 'rent' &&
            typeof signInNavigation.customizeEmmaExperience?.lookingToBuyHome !==
                'boolean' &&
            signInNavigation.customizeEmmaExperience?.lookingToBuyHome !== 'skip') {
            return {
                params: undefined,
                name: 'BuyingPlanScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience
            ?.didSeeProcessingScreenForCustomisationScreen) {
            return {
                params: undefined,
                name: 'ProcessingForCustomisedExperienceScreen',
            };
        }
        if (!signInNavigation.customizeEmmaExperience?.didSeeStarterPlanScreen) {
            return {
                params: undefined,
                name: 'StarterPlanScreen',
            };
        }
    }
    if (!signInNavigation.space &&
        // Check if the user is in any other premium space with accounts
        // We can skip these steps if they are
        Object.values(spaces).find((s) => s.space.accountsCount && s.space.isPremium && !s.space.isDefault) === undefined) {
        if (checkFeatureFlag(featureFlags, 'ab_paywall_before_and_after_choose_bank') &&
            user.guessedHomeCountry === 'GB' &&
            !signInNavigation.premiumScreenSkippedBeforeChooseBank &&
            !signInNavigation.isSigningIn) {
            const firstPremiumScreenRoute = getFirstPremiumScreenRoute({
                isAppStart,
                storePasscode,
                user,
                featureFlags,
                signInNavigation,
                isSecond: false,
                shouldShowStripe,
                hasEmployerBenefit,
            });
            if (firstPremiumScreenRoute) {
                return firstPremiumScreenRoute;
            }
        }
        const bankConnectionsRoute = getBankConnectionsRoute({
            user,
            connections,
            connectingConnection,
            featureFlags,
            signInNavigation,
        });
        if (bankConnectionsRoute) {
            return bankConnectionsRoute;
        }
        if (signInNavigation.showFakeLoadingScreen) {
            return {
                params: undefined,
                name: 'AnimatedLoadingScreen',
            };
        }
        if (!signInNavigation.didSeeSpendingInsights &&
            isEligibleForMiniValueProof({ featureFlags, signInNavigation, user }) &&
            isReady(spendingInsights)) {
            return {
                params: undefined,
                name: 'SpendingInsightsIntroScreen',
            };
        }
        const firstPremiumScreenRoute = getFirstPremiumScreenRoute({
            isAppStart,
            storePasscode,
            user,
            featureFlags,
            signInNavigation,
            isSecond: false,
            shouldShowStripe,
            hasEmployerBenefit,
        });
        if (firstPremiumScreenRoute) {
            return firstPremiumScreenRoute;
        }
        const setupRoute = getSetupRoute({ user, featureFlags, signInNavigation });
        if (setupRoute)
            return setupRoute;
    }
    if (!signInNavigation.isSigningIn &&
        !signInNavigation.didPostStreaksCommitment &&
        checkFeatureFlag(featureFlags, 'streaks_screen_onboarding_v2')) {
        return {
            params: undefined,
            name: 'StreaksIntroScreen',
        };
    }
    return {
        params: undefined,
        name: 'SuccessScreen',
    };
}
// If we call the function from one of these screens, then they are connecting
const isConnectingConnection = (routeName) => routeName === 'ChooseBank' ||
    routeName === 'Consent' ||
    routeName === 'USConsent' ||
    routeName === 'AskAnotherBankAccount';
const resetRoutes = [
    'Name',
    'SetGoal',
    'Attribution',
    'SetupPasscode',
    'SuccessScreen',
    'RefreshConnectionScreen',
    'LinkAnotherLogin',
    'DependentsScreen',
    'PushNotifications',
    'RentQuestionScreen',
    'StreaksIntroScreen',
    'MaritalStatusScreen',
    'AddressPostCodeScreen',
    'AnimatedLoadingScreen',
    'SpendingInsightsIntroScreen',
    'EmploymentStatusScreen',
    'AnnualGrossIncomeScreen',
    'OnboardingHowFreeTrialWorksScreen',
    'ProfilingQuestions',
    'CustomiseExperienceIntro',
    'FinancialFeelingScreen',
    'FinancialFeelingPositiveReinforcementScreen',
    'ResonanceSelectionScreen',
    'MoneyManagementStyleScreen',
    'MoneyManagementStylePositiveReinforcementScreen',
    'PaydayConcernScreen',
    'SpendingTrackingScreen',
    'ExpenseSharingScreen',
    'FinancialAccountsScreen',
    'SavingFrequencyScreen',
    'SaveMoneyPositiveReinforcementScreen',
    'EmmaSavingHelpScreen',
    'InvestmentStatusScreen',
    'RentOrOwnScreen',
    'MortgageInfoScreen',
    'BuyingPlanScreen',
    'CustomizingExperienceLoadingScreen',
    'ProcessingForCustomisedExperienceScreen',
    'StarterPlanScreen',
    'SetupAllYourMoneyScreen',
    'SetupBudgetScreen',
    'SetupSubscriptionsScreen',
];
export const navigateToLoggedIn = async (navigation, { userAdditionalInfo, user, userAddresses, connections, featureFlags, signInNavigation, budgetBaseLimit, resetPasscodeNextStep, hasCurrentRentReporting, spaces, shouldShowStripe, hasEmployerBenefit, spendingInsights, }) => {
    const currentRoutes = navigation.getState()?.routes;
    const currentRoute = 
    // eslint-disable-next-line no-unsafe-optional-chaining
    currentRoutes?.[currentRoutes?.length - 1]?.name || undefined;
    const route = await getNextRoute({
        user,
        userAddresses,
        userAdditionalInfo,
        connections,
        featureFlags,
        connectingConnection: isConnectingConnection(currentRoute),
        signInNavigation,
        budgetBaseLimit,
        resetPasscodeNextStep,
        hasCurrentRentReporting,
        spaces,
        shouldShowStripe,
        hasEmployerBenefit,
        spendingInsights,
    });
    if (resetRoutes.includes(route.name)) {
        navigation.dispatch(CommonActions.reset({
            index: 0,
            routes: [route],
        }));
    }
    else {
        navigation?.navigate(route);
    }
};
